import React, { useState, useEffect } from "react";
import { Modal, Button, Row, Col } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import DatePicker from "@services/ui-components/src/DatePicker/DatePicker";
import { Formik, Form, ErrorMessage } from "formik";
import * as Yup from "yup";
import Spinner from "@services/ui-components/src/Spinner/Spinner";
import {
    useCreateVacationMutation,
    useDeleteVacationMutation,
    useGetEmployeeByIdScheduleQuery,
    useIsAnyBookingsPerDateRangeLazyQuery,
    useUpdateVacationMutation,
} from "@me-team/host/main/work-shedule/graphql/workShedule.hooks";
import { Link, useParams } from "react-router-dom";
import { ApolloError, useReactiveVar } from "@apollo/client";
import { CompanyAccess, PermissionScope, VacationProxy, VacationType } from "@me-team/host/main/graphql/types";
import { currentBranchEmployeeIdVar, currentCompanyIdVar } from "@me-team/host/main/globalVar/state";
import { ErrorModalComponent } from "@me-pos/error-modal";
import i18next from "i18next";
import ErrorService from "../../../../services/ErrorService";
import { ReactSelect, SpinnerLoader } from "@services/ui-components";
import ConfirmDeleteVacationModal from "../ConfirmDeleteVacationModal/ConfirmDeleteVacationModal";
import { useRoleAccess } from "@me-team/host/src/utils/useRoleAccess";

interface Employee {
    id: number;
    surname: string;
    name: string;
    vacations: Array<{ id: number; startDate?: string; endDate?: string; type?: VacationType }>;
}

interface VacationModalProps {
    show: boolean;
    handleClose?: () => void;
    employeeId: number;
    forceRefresh: () => void;
    toggleShowToast: (str: string) => void;
    edit: boolean;
    editDate: any;
    isCancel?: boolean;
}

interface FormValues {
    startDate: Date | null | string;
    endDate: Date | null | string;
    type: { value: VacationType | null; label: VacationType | null };
}

const setToEndOfDay = (date: Date): Date => {
    const endOfDay = new Date(date);
    endOfDay.setHours(23, 59, 59, 999);
    return endOfDay;
};

const validationSchema = (vacations: Employee["vacations"], t: (key: string) => string, edit: boolean) =>
    Yup.object()
        .shape({
            startDate: Yup.date().nullable().required(t("Start date is required")),
            endDate: Yup.date()
                .nullable()
                .required(t("End date is required"))
                .min(Yup.ref("startDate"), t("End date cannot be before start date")),
            type: Yup.object()
                .nullable()
                .required(t("Vacation type is required"))
                .shape({
                    value: Yup.string().required(t("Vacation type is required")),
                }),
        })
        .test(
            "date-overlap",
            t("Vacation has already been added for the specified dates. Edit it."),
            function (values) {
                if (edit) {
                    return true;
                }
                const { startDate, endDate } = values;
                if (!startDate || !endDate) return true;

                const startTimestamp = startDate.getTime();
                const endTimestamp = setToEndOfDay(endDate).getTime();

                for (let vac of vacations) {
                    const vacStart = new Date(vac.startDate.replace(" ", "T"));
                    const vacEnd = setToEndOfDay(new Date(vac.endDate.replace(" ", "T")));
                    const vacStartTimestamp = vacStart.getTime();
                    const vacEndTimestamp = vacEnd.getTime();
                    if (
                        (startTimestamp >= vacStartTimestamp && startTimestamp <= vacEndTimestamp) ||
                        (endTimestamp >= vacStartTimestamp && endTimestamp <= vacEndTimestamp) ||
                        (startTimestamp <= vacStartTimestamp && endTimestamp >= vacEndTimestamp)
                    ) {
                        return this.createError({
                            path: "startDate",
                            message: t("Vacation has already been added for the specified dates. Edit it."),
                        });
                    }
                }
                return true;
            }
        );

const formatDate = (date: Date): string => {
    return `${String(date.getDate()).padStart(2, "0")}.${String(date.getMonth() + 1).padStart(2, "0")}.${date.getFullYear()}`;
};

const formatDateRange = (startDateStr: string, endDateStr: string): string => {
    const startDate = new Date(startDateStr.replace(" ", "T") + "Z");
    const endDate = new Date(endDateStr.replace(" ", "T") + "Z");

    return `${formatDate(startDate)} – ${formatDate(endDate)}`;
};

const BOOKING_FOR_DATES_CATEGORY = "work_schedule.is_booking.for_dates";
const BOOKING_FOR_DATES_MESSAGE = "There is a booking for these dates";

const isBookingForDatesError = (error: unknown): boolean => {
    if (!(error instanceof ApolloError)) {
        return false;
    }

    return error.graphQLErrors.some(
        (graphQLError) =>
            graphQLError.extensions?.category === BOOKING_FOR_DATES_CATEGORY ||
            graphQLError.message === BOOKING_FOR_DATES_MESSAGE
    );
};

type VacationBlockingState = {
    branchNames: string[];
};

interface PreviousVacationsListProps {
    vacations: Employee["vacations"];
    onDelete: (vacationId: number) => void;
    canDeleteVacation: boolean;
    t: (key: string) => string;
}

const PreviousVacationsList: React.FC<PreviousVacationsListProps> = ({ vacations, onDelete, canDeleteVacation, t }) => {
    if (!vacations || !vacations.length) {
        return <span className="fs-7">{t("MissedAddVacationModal")}</span>;
    }

    return (
        <>
            {[...vacations]
                .sort((a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime())
                .map((vac, index) => (
                    <div className={`d-flex justify-content-between ${index !== 0 ? "pt-3" : ""}`} key={vac.id}>
                        <div className="fs-7">
                            <span>{formatDateRange(vac.startDate, vac.endDate)}</span>
                            <span> - {t(vac.type)}</span>
                        </div>
                        {canDeleteVacation && new Date(vac.startDate) > new Date() && (
                            <i
                                className="bi bi-trash3"
                                data-bs-title={t("Delete vacation")}
                                data-bs-placement="bottom"
                                onClick={() => onDelete(vac.id)}
                                style={{ cursor: "pointer", width: "14px", height: "16px", fontSize: "14px" }}
                            ></i>
                        )}
                    </div>
                ))}
        </>
    );
};

interface DatePickerFieldProps {
    id: string;
    name: string;
    label: string;
    selected: Date | null;
    onChange: (date: Date) => void;
    errorMessage: string | undefined;
    t: (key: string) => string;
    todayButton?: string;
    isClearable?: boolean;
    hasError?: boolean;
    disabled?: boolean;
}

const DatePickerField: React.FC<DatePickerFieldProps> = ({
    id,
    name,
    label,
    selected,
    onChange,
    errorMessage,
    t,
    todayButton,
    isClearable,
    hasError,
    disabled,
}) => {
    const parseDate = (date: Date | string | null): Date | null => {
        if (!date) return null;

        if (date instanceof Date) return date;

        if (typeof date === "string") {
            return new Date(date.replace(" ", "T"));
        }
        return null;
    };

    const parsedSelected = parseDate(selected);
    const hasClearIcon = Boolean(isClearable && parsedSelected);

    return (
        <Row className="pt-3">
            <Col sm={3} className="pe-0 pt-2 mb-2 mb-sm-0">
                <label htmlFor={id}>
                    <strong className="text-dark fs-7 required-label">{t(label)}</strong>
                </label>
            </Col>
            <Col sm={9} className="text-dark">
                <div className="position-relative">
                    <DatePicker
                        id={id}
                        name={name}
                        className={`form-control fs-7 py-6 px-6 ${hasError ? "is-invalid border-danger" : ""}`}
                        wrapperClassName={`w-100 datepicker-with-icon ${hasClearIcon ? "datepicker-with-icon--clear" : ""}`}
                        selected={parseDate(selected)}
                        placeholderText={t("dd.mm.yyyy")}
                        onChange={onChange}
                        dateFormat="dd.MM.yyyy"
                        calendarStartDay={1}
                        maxDate={new Date("12-27-9999")}
                        todayButton={todayButton}
                        isClearable={isClearable}
                        disabled={disabled}
                        icon={<i className="bi bi-calendar4-week position-absolute end-0  pe-6 pt-6"></i>}
                        showIcon
                        toggleCalendarOnIconClick
                    />
                </div>
                {errorMessage && <p className="fs-7 p-1 text-danger m-0">{errorMessage}</p>}
            </Col>
        </Row>
    );
};
const AddVacationModal: React.FC<VacationModalProps> = ({
    show,
    handleClose,
    employeeId,
    forceRefresh,
    toggleShowToast,
    edit,
    editDate,
    isCancel,
}) => {
    const { t } = useTranslation();
    const { branchId } = useParams();
    const currentCompanyId = useReactiveVar(currentCompanyIdVar);
    const currentBranchEmployeeId = useReactiveVar(currentBranchEmployeeIdVar);
    const [isErrorModalOpen, setIsErrorModalOpen] = useState<number>(null);
    const { data, loading, refetch } = useGetEmployeeByIdScheduleQuery({
        variables: { companyId: +currentCompanyId, branchId: Number(branchId), employeeId },
        skip: !currentCompanyId,
        context: {
            errorType: "global",
        },
    });
    const [createVacation] = useCreateVacationMutation({
        context: {
            errorType: "local",
        },
    });
    const [deleteVac] = useDeleteVacationMutation({
        context: {
            errorType: "local",
        },
    });
    const [updateVacation] = useUpdateVacationMutation({
        context: {
            errorType: "local",
        },
    });
    const [checkBookingsPerDateRange] = useIsAnyBookingsPerDateRangeLazyQuery({
        fetchPolicy: "network-only",
        context: {
            errorType: "local",
        },
    });
    const employee = data?.user?.company?.employees?.employees[0];
    const [displayedVacations, setDisplayedVacations] = useState<Employee["vacations"]>([]);
    const [blockingState, setBlockingState] = useState<VacationBlockingState | null>(null);
    const [editVacations, setEditVacations] = useState(null);
    const [confirmDeleteVacationId, setConfirmDeleteVacationId] = useState<number | null>(null);
    const [isConfirmDeleteVacationOpen, setIsConfirmDeleteVacationOpen] = useState(false);
    const [isDeletingVacation, setIsDeletingVacation] = useState(false);
    const isVacationModalHidden = isConfirmDeleteVacationOpen;
    const isVacationBlocked = blockingState !== null;
    const isOwnVacationEmployee = Boolean(
        currentBranchEmployeeId && employeeId && currentBranchEmployeeId === employeeId
    );
    const vacationActionScope = isOwnVacationEmployee ? PermissionScope.Self : PermissionScope.All;
    const canCreateVacationForSelectedEmployee = useRoleAccess(CompanyAccess.VacationCreate, {
        scope: vacationActionScope,
    });
    const canEditVacationForSelectedEmployee = useRoleAccess(CompanyAccess.VacationEdit, {
        scope: vacationActionScope,
    });
    const canDeleteVacationForSelectedEmployee = useRoleAccess(CompanyAccess.VacationDelete, {
        scope: vacationActionScope,
    });
    const shouldDisableVacationForm = Boolean(edit && !canEditVacationForSelectedEmployee);
    const shouldShowDeleteVacationAction = canDeleteVacationForSelectedEmployee;

    const vacationTypeOptions = Object.entries(VacationType).map(([, value]) => ({
        value,
        label: t(value),
    }));
    const checkDateOverlap = (vacations: VacationProxy[], editDateStart: string) => {
        const editStartDate = Date.parse(editDateStart.slice(0, 10));
        for (const vacation of vacations) {
            const vacationStart = Date.parse(vacation.startDate.slice(0, 10));
            const vacationEnd = Date.parse(vacation.endDate.slice(0, 10));
            if (editStartDate >= vacationStart && editStartDate <= vacationEnd) {
                setEditVacations(vacation);
                return vacation;
            }
        }
        return null;
    };

    const handleOpenDeleteVacationConfirm = (vacationId: number) => {
        if (!shouldShowDeleteVacationAction) {
            return;
        }
        setConfirmDeleteVacationId(vacationId);
        setIsConfirmDeleteVacationOpen(true);
    };

    const handleCancelDeleteVacationConfirm = () => {
        if (isDeletingVacation) return;
        setIsConfirmDeleteVacationOpen(false);
        setConfirmDeleteVacationId(null);
    };

    const handleConfirmDeleteVacation = async (vacationId: number) => {
        if (!canDeleteVacationForSelectedEmployee) {
            return;
        }
        const isDeletingEditedVacation = editVacations?.id === vacationId;
        setIsDeletingVacation(true);
        setDisplayedVacations((prevVacations) => prevVacations.filter((vac) => vac.id !== vacationId));

        try {
            await deleteVac({
                variables: { vacationId, branchId: +branchId, employeeId },
                context: { errorType: "local" },
                onError: (error: ApolloError) => {
                    setIsErrorModalOpen(ErrorService.errorHandling(error));
                },
            });

            await refetch();
            forceRefresh();
            toggleShowToast(t("Deleted"));

            setIsConfirmDeleteVacationOpen(false);
            setConfirmDeleteVacationId(null);

            // If you deleted the vacation you were editing, close everything
            if (isDeletingEditedVacation) {
                handleClose?.();
            }
        } finally {
            setIsDeletingVacation(false);
        }
    };

    useEffect(() => {
        if (employee?.vacations) {
            edit && checkDateOverlap(employee?.vacations, editDate?.startDate);
            setDisplayedVacations(employee?.vacations);
        }
    }, [employee, edit, editDate?.startDate]);

    if (loading) {
        return (
            <Modal size="lg" show={true}>
                <Spinner />
            </Modal>
        );
    }

    const formatDateForInput = (date: Date | string): string => {
        if (typeof date === "string" && /^\d{4}-\d{2}-\d{2}$/.test(date.slice(0, 10))) {
            return date.slice(0, 10);
        }
        if (date instanceof Date) {
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, "0");
            const day = String(date.getDate()).padStart(2, "0");
            return `${year}-${month}-${day}`;
        }
        throw new Error("Invalid date format");
    };

    const submitVacationMutation = async (values: FormValues) => {
        if (edit) {
            await updateVacation({
                variables: {
                    vacationId: editVacations?.id,
                    input: {
                        startDate: formatDateForInput(values.startDate),
                        endDate: formatDateForInput(values.endDate),
                        type: values.type.value,
                    },
                    branchId: +branchId,
                    employeeId: employeeId,
                },
            });
            return;
        }

        await createVacation({
            variables: {
                employeeId: employeeId,
                input: {
                    startDate: formatDateForInput(values.startDate),
                    endDate: formatDateForInput(values.endDate),
                    type: values.type.value,
                },
                branchId: +branchId,
            },
        });
    };

    const handleFormSubmit = async (values: FormValues) => {
        if ((edit && !canEditVacationForSelectedEmployee) || (!edit && !canCreateVacationForSelectedEmployee)) {
            return;
        }

        if (!values.startDate || !values.endDate) {
            return;
        }

        try {
            const { data: bookingsData, error: bookingsError } = await checkBookingsPerDateRange({
                variables: {
                    branchId: +branchId,
                    employeeId,
                    startDate: formatDateForInput(values.startDate),
                    endDate: formatDateForInput(values.endDate),
                },
            });

            if (bookingsError) {
                setIsErrorModalOpen(ErrorService.errorHandling(bookingsError));
                return;
            }

            const branchesWithBookings = bookingsData?.isAnyBookingsPerDateRange ?? [];
            if (branchesWithBookings.length) {
                setBlockingState({ branchNames: branchesWithBookings.map((branch) => branch.name) });
                return;
            }

            await submitVacationMutation(values);
            await refetch();
            forceRefresh();
            toggleShowToast(t("Saved"));
            handleClose();
        } catch (error) {
            if (isBookingForDatesError(error)) {
                setBlockingState({ branchNames: [] });
                return;
            }

            setIsErrorModalOpen(ErrorService.errorHandling(error as ApolloError));
        }
    };

    const initStartDate = () => {
        if (edit) {
            return editVacations?.startDate;
        }
        if (isCancel) {
            return editDate.toJSDate();
        }
        return null;
    };

    return (
        <>
            <Modal
                size="lg"
                show={show}
                onHide={handleClose}
                centered
                style={{
                    opacity: isVacationModalHidden ? 0 : 1,
                    pointerEvents: isVacationModalHidden ? "none" : "auto",
                }}
            >
                <Modal.Header className="border-0" closeButton>
                    {isVacationBlocked ? (
                        <h5 className="m-0">{edit ? t("Vacation editing denied") : t("Vacation creation denied")}</h5>
                    ) : (
                        <h5 className="m-0"> {edit ? t("Edit vacation") : t("Add Vacation")} </h5>
                    )}
                </Modal.Header>
                <Formik
                    key={editVacations?.id || "new-form"}
                    initialValues={{
                        startDate: initStartDate(),
                        endDate: edit ? editVacations?.endDate : null,
                        type: edit ? vacationTypeOptions.find((t) => t.value == editVacations?.type) : null,
                    }}
                    validationSchema={validationSchema(displayedVacations || [], t, edit)}
                    onSubmit={handleFormSubmit}
                >
                    {({ setFieldValue, values, errors, touched, isSubmitting }) => (
                        <Form noValidate>
                            {isVacationBlocked ? (
                                <Modal.Body style={{ padding: "16px 28px" }}>
                                    <Row className="justify-content-center text-center">
                                        <span>
                                            {edit
                                                ? t(
                                                      "Unable to edit vacation because the employee has bookings for the selected days. Go to the logbook to reschedule or cancel the bookings."
                                                  )
                                                : t(
                                                      "Unable to create vacation because the employee has bookings for the selected days. Go to the logbook to reschedule or cancel the bookings."
                                                  )}
                                        </span>
                                    </Row>
                                    {blockingState.branchNames.length > 0 && (
                                        <Row className="justify-content-center text-center pt-3">
                                            <span>
                                                {t("Records also exist in branches: {{branches}}.", {
                                                    branches: blockingState.branchNames.join(", "),
                                                })}
                                            </span>
                                        </Row>
                                    )}
                                    <div className="border-0 col-12">
                                        <div className="d-flex px-3 justify-content-center">
                                            <div className="col-12 col-sm-6">
                                                <Link
                                                    className="btn btn-primary fw-bold w-100 fs-7 mt-4"
                                                    to={`/logbook/journal/${branchId}`}
                                                >
                                                    {t("Go to Logbook")}
                                                </Link>
                                            </div>
                                        </div>
                                    </div>
                                </Modal.Body>
                            ) : (
                                <>
                                    <Modal.Body style={{ padding: "16px 28px" }}>
                                        <Row className="pt-3 pb-sm-3">
                                            <Col sm={3} className="mb-2 mb-sm-0">
                                                <strong>
                                                    <span className="fs-7">{t("Employee")}</span>
                                                </strong>
                                            </Col>
                                            <Col sm={9}>
                                                <span className="fs-7">
                                                    {employee?.name} {employee?.surname}
                                                </span>
                                            </Col>
                                        </Row>

                                        <DatePickerField
                                            id="startDate"
                                            name="startDate"
                                            label={t("Start Date")}
                                            selected={values.startDate}
                                            onChange={(date) => setFieldValue("startDate", date)}
                                            errorMessage={
                                                touched.startDate && errors.startDate
                                                    ? String(errors.startDate)
                                                    : undefined
                                            }
                                            t={t}
                                            hasError={touched.startDate && !!errors.startDate}
                                            todayButton={t("Today")}
                                            isClearable
                                            disabled={shouldDisableVacationForm}
                                        />

                                        <DatePickerField
                                            id="endDate"
                                            name="endDate"
                                            label={t("End Date")}
                                            selected={values.endDate}
                                            onChange={(date) => setFieldValue("endDate", date)}
                                            errorMessage={
                                                touched.endDate && errors.endDate ? String(errors.endDate) : undefined
                                            }
                                            t={t}
                                            hasError={touched.startDate && !!errors.startDate}
                                            todayButton={t("Today")}
                                            isClearable
                                            disabled={shouldDisableVacationForm}
                                        />

                                        <Row className="pt-3">
                                            <Col sm={3} className="pe-0 mb-2 mb-sm-0">
                                                <label htmlFor="previousVacation">
                                                    <strong className="text-dark fs-7 required-label">
                                                        {t("Type")}
                                                    </strong>
                                                </label>
                                            </Col>
                                            <Col sm={9} className="text-dark">
                                                <ReactSelect
                                                    id="addVacationModal-vacationType-field"
                                                    name="type"
                                                    options={vacationTypeOptions}
                                                    value={values.type}
                                                    onChange={(option) => setFieldValue("type", option)}
                                                    placeholder={t("Select vacation type")}
                                                    isSearchable={true}
                                                    disabled={shouldDisableVacationForm}
                                                />
                                            </Col>
                                            <Col sm={9} className="ms-sm-auto">
                                                <ErrorMessage name="type">
                                                    {(msg) => <div className="invalid-feedback d-block">{msg}</div>}
                                                </ErrorMessage>
                                            </Col>
                                        </Row>

                                        <Row className="pt-3">
                                            <Col sm={3} className="pe-0 mb-2 mb-sm-0">
                                                <label htmlFor="previousVacation">
                                                    <strong className="text-dark fs-7">{t("Previous Vacation")}</strong>
                                                </label>
                                            </Col>
                                            <Col sm={9} className="text-dark">
                                                <PreviousVacationsList
                                                    vacations={displayedVacations}
                                                    onDelete={handleOpenDeleteVacationConfirm}
                                                    canDeleteVacation={shouldShowDeleteVacationAction}
                                                    t={t}
                                                />
                                            </Col>
                                        </Row>
                                    </Modal.Body>

                                    <div className="p-6">
                                        <Row className="justify-content-end gx-2">
                                            <Col xs={6} lg={3}>
                                                <Button
                                                    variant="outline-dark"
                                                    className="w-100 h-100 fw-normal"
                                                    onClick={handleClose}
                                                    disabled={isSubmitting}
                                                >
                                                    {t("Cancel")}
                                                </Button>
                                            </Col>
                                            <Col xs={6} lg={3}>
                                                <Button
                                                    type="submit"
                                                    variant="primary"
                                                    className="w-100 text-truncate"
                                                    disabled={
                                                        isSubmitting ||
                                                        shouldDisableVacationForm ||
                                                        (!edit && !canCreateVacationForSelectedEmployee)
                                                    }
                                                >
                                                    {isSubmitting ? (
                                                        <SpinnerLoader
                                                            variant="light"
                                                            loadingText={t("Confirming...")}
                                                        />
                                                    ) : (
                                                        t("Confirm")
                                                    )}
                                                </Button>
                                            </Col>
                                        </Row>
                                    </div>
                                </>
                            )}
                        </Form>
                    )}
                </Formik>
            </Modal>
            <ConfirmDeleteVacationModal
                show={isConfirmDeleteVacationOpen}
                vacationId={confirmDeleteVacationId}
                onCancel={handleCancelDeleteVacationConfirm}
                onConfirm={handleConfirmDeleteVacation}
                isDeleting={isDeletingVacation}
            />
            {isErrorModalOpen ? (
                <ErrorModalComponent
                    i18n={i18next}
                    onClose={() => {
                        setIsErrorModalOpen(null);
                    }}
                    isOpen={!!isErrorModalOpen}
                    currentError={isErrorModalOpen}
                />
            ) : null}
        </>
    );
};

export default AddVacationModal;
